import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormLabel, FormError, FormHelpText } from 'components/FormTexts';
import * as Styled from './Textarea.styled';

const Textarea = (props) => {
  const {
    className,
    classes,
    name,
    placeholder,
    label,
    helpText,
    error,
    disabled,
    verified,
    resizeable,
    maxlength,
    rows,
    inputRef,
    onFocus,
    onBlur,
    labelId,
    ...passThroughProps
  } = props;

  // Used when adornment is present.
  const [focused, setFocused] = useState(false);

  const handleFocus = useCallback(
    (event) => {
      if (onFocus) {
        onFocus(event);
      }
      setFocused(true);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (event) => {
      if (onBlur) {
        onBlur(event);
      }
      setFocused(false);
    },
    [onBlur]
  );

  // Determine whether to show red/green/grey border and inline icons.
  const inputState = useMemo(() => {
    if (error) return 'error';
    return verified ? 'verified' : '';
  }, [error, verified]);

  const StyledInputComponent = Styled.BaseInput;

  return (
    <Styled.RootComponent className={classnames(classes.root, className)}>
      {label && (
        <FormLabel id={labelId} className={classes.label}>
          {label}
        </FormLabel>
      )}
      {helpText && (
        <FormHelpText className={classes.helpText}>{helpText}</FormHelpText>
      )}
      <Styled.InputWrapper
        state={inputState}
        disabled={disabled}
        focused={focused ? 'true' : ''}
      >
        <Styled.InputContainer>
          <StyledInputComponent
            state={inputState}
            className={classes.input}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            resizeable={resizeable}
            maxLength={maxlength}
            rows={rows}
            ref={inputRef}
            onBlur={handleBlur}
            onFocus={handleFocus}
            aria-labelledby={labelId}
            {...passThroughProps}
          />
        </Styled.InputContainer>
      </Styled.InputWrapper>
      {error && typeof error === 'string' && <FormError>{error}</FormError>}
    </Styled.RootComponent>
  );
};

Textarea.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // if boolean then no error in shown below.
  classes: PropTypes.shape({
    root: PropTypes.string,
    input: PropTypes.string,
    label: PropTypes.string,
    helpText: PropTypes.string,
    error: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  verified: PropTypes.bool,
  resizeable: PropTypes.bool,
  maxlength: PropTypes.number,
  rows: PropTypes.number,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  labelId: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

Textarea.defaultProps = {
  className: '',
  placeholder: '',
  label: '',
  helpText: '',
  disabled: false,
  error: false,
  verified: false,
  resizeable: true,
  maxlength: 524288, //The maximum number of characters allowed in the <input> element. Default value is 524288
  rows: 2,
  inputRef: null,
  classes: {
    root: '',
    input: '',
    label: '',
    error: '',
  },
  labelId: '',
};

export default Textarea;
